// This is a component for comparing differences much like the diff view in VSCode. However, instead of a
// view comparing code to keep / change, it's used to merge elements together by giving the user a choice
// of which table fields to keep and which to get rid of, resulting in a full object.

// For example, a Transaction could be provided that was incomplete - the user would be given multiple choices,
// and these choices will be given back as an array of any type once the user clicked all of the changes to accept.
// There will also be editing control available as a prop, so that the user can edit the fields in the table.

// There is a "View Merged Transaction" feature that can be toggled on and off in the props that is bound to specifically
// transactions, however.

import { type Transaction } from '@kalos/kalos-rpc';
import { Dialog, DialogContent } from '@kalos/ui';
import Typography from '@mui/material/Typography';
import { type FC, type ReactNode, useCallback, useEffect, useState } from 'react';

import { useTimesheetDepartmentListQuery } from '../../../hooks/react-query/useTimesheetDepartmentQuery';
import { useTransactionAccountGet } from '../../../hooks/react-query/useTransactionAccountsQuery';
import { useUserQuery } from '../../../hooks/react-query/useUserQuery';
import { useVendorGetQuery } from '../../../hooks/react-query/useVendorQuery';
import { Button } from '../Button';
import { EditTransaction } from '../EditTransaction';
import { type Columns, type Data, InfoTable } from '../InfoTable';
import { Modal } from '../Modal';
import { PlainForm, type Schema } from '../PlainForm';
import { SectionBar } from '../SectionBar';

export interface SelectedChoice {
  value: string;
  fieldName: string;
  fieldIndex: number | undefined;
}

const DepartmentDisplay = ({ departmentId }: { departmentId: number }) => {
  const departmentQuery = useTimesheetDepartmentListQuery({
    filter: {
      isActive: 1,
    },
  });
  return (
    departmentQuery.data?.results.find((department) => department.id === departmentId)?.value ??
    departmentId
  );
};

const UserDisplay = ({ userId }: { userId: number }) => {
  const userQuery = useUserQuery({
    filters: {
      id: userId,
    },
  });
  return userQuery.data ? `${userQuery.data.firstname} ${userQuery.data.lastname}` : userId;
};

const CostCenterDisplay = ({ costCenterId }: { costCenterId: number }) => {
  const costCenterQuery = useTransactionAccountGet({
    filters: {
      id: costCenterId,
    },
  });

  return costCenterQuery.data?.description ?? costCenterId;
};

const VendorDisplay = ({ vendorId }: { vendorId: number }) => {
  const vendorQuery = useVendorGetQuery({
    filters: {
      id: vendorId,
    },
  });
  return vendorQuery.data?.vendorName ?? vendorId;
};

interface Props {
  columnHeaders: Columns;
  rows: {
    choices: string[];
    rowIndex: number;
    rowName: string; // What is the label to display to the side?
    onSelect?: (selected: string) => void;
  }[];
  onSubmit: (results: SelectedChoice[]) => void; // Index of the results is the index of the relevant row
  onCancel: () => void;
  properNames?: Record<string, React.ReactNode>; // Just objects with key-value pairs that can be used to correct row names where applicable
  viewMergedTransaction?: boolean; // Can you view the merged transaction if applicable?
  transaction?: Transaction; // Optional, passed in for above
  onSaveMergedTransaction?: (transaction: Transaction) => void;
  onChangeTransaction?: (newTxn: Transaction) => void;
  loading?: boolean;
}

type Field = {
  fieldData: string;
};

const getStatus = (key?: number) => {
  if (key === 3) {
    return 'Accepted';
  } else if (key === 4) {
    return 'Rejected';
  } else if (key === 5) {
    return 'Processed';
  } else {
    return 'Pending';
  }
};

export const MergeTable: FC<Props> = ({
  columnHeaders,
  rows,
  onSubmit,
  onCancel,
  properNames,
  viewMergedTransaction,
  transaction,
  onSaveMergedTransaction,
  onChangeTransaction,
  loading,
}) => {
  let updatedFieldData = ''; // Used when updating field info, not put into a state operation to avoid re-rendering unnecessarily
  // Index of the array is the index of the relevant row
  const [selectedChoices, setSelectedChoices] = useState<SelectedChoice[]>(
    rows.map(() => {
      return { value: '', fieldName: '', fieldIndex: undefined };
    }), // Actually proud of how this one works not gonna lie
  );
  const [data, setData] = useState<Data>();
  const [selectAllPromptOpen, setSelectAllPromptOpen] = useState<boolean>(false);
  const [transactionToView, setTransactionToView] = useState<Transaction | undefined>(); // For the viewMergedTransaction part
  const [fieldToEdit, setFieldToEdit] = useState<
    | {
        rowIndex: number;
        choice: string;
      }
    | undefined
  >(undefined);

  const field: Field = {
    fieldData: fieldToEdit != undefined ? fieldToEdit?.choice : '',
  };

  const handleSetTransactionToView = useCallback(
    (transactionToView: Transaction | undefined) => {
      setTransactionToView(transactionToView);
    },
    [setTransactionToView],
  );

  const handleSetSelectedChoiceIndices = useCallback(
    (selectedChoice: SelectedChoice, rowIndex: number) => {
      const sci = selectedChoices;
      // eslint-disable-next-line react-compiler/react-compiler
      sci[rowIndex] = selectedChoice;
      setSelectedChoices(sci);
      if (transaction) {
        const txn = transaction;
        // @ts-ignore
        txn[selectedChoice.fieldName as keyof Transaction] =
          selectedChoice.fieldName === 'jobId'
            ? Number(selectedChoice.value)
            : selectedChoice.value;
        if (onChangeTransaction) onChangeTransaction(txn!);
      }
    },
    [setSelectedChoices, selectedChoices, onChangeTransaction, transaction],
  );
  const handleSetFieldToEdit = useCallback(
    (fieldToEdit: { rowIndex: number; choice: string } | undefined) => {
      setFieldToEdit(fieldToEdit);
    },
    [setFieldToEdit],
  );

  const handleSetData = useCallback(() => {
    const rowChoices: { value: ReactNode; onClick: () => void }[][] = [];

    rows.forEach((row, rowIndex) => {
      rowChoices.push([
        {
          // @ts-ignore
          value: (
            <Typography>
              {
                //@ts-ignore
                properNames[row.rowName]
                  ? //@ts-ignore
                    properNames[row.rowName]
                  : row.rowName
              }
            </Typography>
          ),
          onClick: () => {},
        },
        ...row.choices.map((choice) => {
          const costCenterID = Number(choice);
          let label = choice;
          let children = null;
          if (row.rowName === 'departmentId') {
            children = costCenterID ? (
              <DepartmentDisplay departmentId={costCenterID} />
            ) : (
              'No department'
            );
            label = '';
          }
          if (row.rowName === 'ownerId' || row.rowName === 'assignedEmployeeId') {
            children = <UserDisplay userId={costCenterID} />;
            label = '';
          }
          if (row.rowName === 'costCenterId') {
            children = costCenterID ? (
              <CostCenterDisplay costCenterId={costCenterID} />
            ) : (
              'No Cost Center'
            );
            label = '';
          }
          if (row.rowName === 'statusId') {
            children = <span>{getStatus(costCenterID)}</span>;
            label = '';
          }
          if (row.rowName === 'vendorId') {
            children = +choice ? <VendorDisplay vendorId={costCenterID} /> : 'No vendor';
            label = '';
          }
          return {
            value: (
              <Button
                style={{ textTransform: 'none' }}
                label={label}
                onClick={() => {
                  handleSetSelectedChoiceIndices(
                    {
                      value: choice,
                      fieldName: rows[rowIndex].rowName,
                      fieldIndex: rows[rowIndex].rowIndex,
                    },
                    rowIndex,
                  );
                  handleSetData();
                }}
                disabled={
                  (selectedChoices[rowIndex].value !== '' &&
                    selectedChoices[rowIndex].value !== choice) ||
                  loading
                }
              >
                {children}
              </Button>
            ),
            // actions: (
            //   <Tooltip content="Edit field">
            //     <IconButton
            //       size="small"
            //       onClick={() =>
            //         handleSetFieldToEdit({ rowIndex: rowIndex, choice: choice })
            //       }
            //     >
            //       <EditIcon />
            //     </IconButton>
            //   </Tooltip>
            // ),
            onClick: () => {
              handleSetSelectedChoiceIndices(
                {
                  value: choice,
                  fieldName: rows[rowIndex].rowName,
                  fieldIndex: rows[rowIndex].rowIndex,
                },
                rowIndex,
              );
              handleSetData();
            },
          };
        }),
      ]);
    });
    setData(rowChoices as Data);
  }, [rows, properNames, selectedChoices, loading, handleSetSelectedChoiceIndices]);

  const handleMerge = useCallback(
    (submission: SelectedChoice[]) => {
      if (selectedChoices.filter((choice) => choice.value != '').length < rows.length) {
        setSelectAllPromptOpen(true);
        return;
      }

      onSubmit(submission.sort((a, b) => (a.fieldIndex! < b.fieldIndex! ? -1 : 1)));
    },
    [onSubmit, selectedChoices, setSelectAllPromptOpen, rows.length],
  );

  const handleSetSelectAllPromptOpen = useCallback(
    (setOpen: boolean) => setSelectAllPromptOpen(setOpen),
    [setSelectAllPromptOpen],
  );

  const handleUpdateChoice = useCallback(
    (
      fieldToEdit: {
        rowIndex: number;
        choice: string;
      },
      updatedChoice: string,
    ) => {
      const rowsNew = rows;
      rowsNew[fieldToEdit.rowIndex] = {
        ...rows[fieldToEdit.rowIndex],
        choices: rows[fieldToEdit.rowIndex].choices.map((choiceIn) => {
          if (choiceIn == fieldToEdit.choice) {
            return updatedChoice;
          }
          return choiceIn;
        }),
      };

      const newChoices = selectedChoices.map((choiceIn) => {
        if (
          choiceIn.value == fieldToEdit.choice &&
          rows[fieldToEdit.rowIndex].rowName == choiceIn.fieldName
        ) {
          const choiceToReturn = choiceIn;
          choiceToReturn.value = updatedChoice;
          return choiceToReturn;
        }
        return choiceIn;
      });

      setSelectedChoices(newChoices);

      handleSetFieldToEdit(undefined);
      handleSetData();
    },
    [rows, handleSetFieldToEdit, handleSetData, setSelectedChoices, selectedChoices],
  );

  useEffect(() => {
    handleSetData();
  }, [handleSetData]);

  const SCHEMA: Schema<Field> = [
    [
      {
        label: 'Field',
        name: 'fieldData',
        type: 'text',
      },
    ],
  ];

  const ViewMergedTransaction = viewMergedTransaction
    ? [
        {
          label: 'View Merged Transaction',
          onClick: () => handleSetTransactionToView(transaction),
          disabled:
            selectedChoices.filter((choice) => choice.fieldName).length !== rows.length || loading,
        },
      ]
    : [];
  return (
    <>
      {transactionToView && (
        <Dialog
          open={!!transactionToView}
          onOpenChange={(open) => {
            if (!loading && !open) {
              handleSetTransactionToView(undefined);
            }
          }}
        >
          <DialogContent className="max-h-[80vh] max-w-max overflow-auto p-0" showClose={false}>
            {transactionToView && (
              <EditTransaction
                transactionInput={transactionToView}
                disabled
                onSave={(saved) => {
                  onSaveMergedTransaction!(saved);
                  handleSetTransactionToView(undefined);
                }}
                onClose={() => handleSetTransactionToView(undefined)}
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      {fieldToEdit && (
        <Modal
          open={fieldToEdit != undefined}
          onClose={() => !loading && handleSetFieldToEdit(undefined)}
        >
          <SectionBar
            actions={[
              {
                label: 'OK',
                onClick: () => handleUpdateChoice(fieldToEdit, updatedFieldData),
              },
              {
                label: 'Cancel',
                onClick: () => handleSetFieldToEdit(undefined),
              },
            ]}
          />
          <PlainForm
            key={rows.toString()}
            schema={SCHEMA}
            data={field}
            onChange={(data) => (updatedFieldData = data.fieldData)}
          />
        </Modal>
      )}
      <Dialog
        open={selectAllPromptOpen}
        onOpenChange={(open) => {
          if (!open) handleSetSelectAllPromptOpen(open);
        }}
      >
        <DialogContent>
          <h3 className="pb-2 text-xl font-semibold">Notice</h3>
          <p>Please select a choice for all of the fields to merge</p>
        </DialogContent>
      </Dialog>
      <SectionBar
        actions={[
          ...ViewMergedTransaction,
          {
            label: 'Merge (Create New)',
            onClick: () => handleMerge(selectedChoices),
            loading,
            disabled: loading,
          },
          { label: 'Cancel', onClick: () => onCancel(), disabled: loading },
        ]}
        title="Resolve Conflicts to Merge"
        fixedActions
      />
      <InfoTable key={selectedChoices.toString()} columns={columnHeaders} data={data} />
    </>
  );
};
